import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import useMakeRequest from "../../utils/ApiHelper";
import { ApiConstants } from "../../constants/ApiConstants";
import FrontLoader from "../layout/FrontLoader";
import { LanguageConstants } from "../../constants/LanguageConstants";
import { Routes as AppRoutes } from "../../constants/RouteConstants";
import { Config } from "../../config/Config";
import { saveStripeCheckoutSessionId } from "../../utils/stripeCheckout";
import {
  CurrencyConvertHistory,
  CurrencyIcon,
  decodeID,
  formatDisplayLongDate,
  getPriceHelper,
} from "../../utils/Helper";
import {
  buildOrderDeliveryAddressLines,
  getCheckoutItemCount,
  getOrderResidenceLabel,
  getOrderShippingInstructions,
  getQuotationTaxAmount,
} from "../../utils/quotationCheckout";
import { canRetryOnlinePayment } from "../../utils/orderStatus";
import { socket } from "../../utils/Socket";
import truckIcon from "../../Assets/images/Truck.svg";
import OrderSuccessOverlay from "../layout/OrderSuccessOverlay";
import {
  parseChargeLineAmount,
  parseOtherChargesLines,
} from "../myquotation/view-quotation/helpers";
import "../myquotation/QuotationCheckout.scss";

function OrderCheckout() {
  const { id } = useParams();
  const navigate = useNavigate();
  const orderView = useMakeRequest();
  const retryPaymentReq = useMakeRequest();

  const [currency, setCurrency] = useState<"USD" | "EUR">("USD");
  const [payMethod, setPayMethod] = useState("");
  const [error, setError] = useState("");
  const [otherChargesModalOpen, setOtherChargesModalOpen] = useState(false);
  const [wireSuccessOpen, setWireSuccessOpen] = useState(false);

  const sawOrderLoadingRef = useRef(false);
  const redirectedRef = useRef(false);
  const wireSuccessShownRef = useRef(false);

  const orderUuid = id
    ? decodeID(decodeURIComponent(String(id)))
    : "";

  useEffect(() => {
    sawOrderLoadingRef.current = false;
    if (!orderUuid) return;
    orderView.makeRequest({
      payload: { order_id: orderUuid },
      url: ApiConstants?.OrderDetail,
      toastStatus: false,
    });
  }, [orderUuid]);

  useEffect(() => {
    if (orderView.loading) {
      sawOrderLoadingRef.current = true;
    }
  }, [orderView.loading]);

  const orderData = orderView?.data?.data;
  const quotation = orderData?.quotation_detail;
  const orderReady = Boolean(orderView?.status && orderData && quotation);
  const orderFetchSettled =
    Boolean(orderUuid) && !orderView.loading && sawOrderLoadingRef.current;
  const showLoader = Boolean(orderUuid) && !orderReady && !orderFetchSettled;

  useEffect(() => {
    if (!orderReady || redirectedRef.current) return;
    if (!canRetryOnlinePayment(orderData)) {
      redirectedRef.current = true;
      toast.error(
        LanguageConstants?.Payment_Failed2 ??
          "This order is not eligible for online payment retry.",
      );
      if (id) {
        navigate(
          AppRoutes.OrderDetail.replace(":id", encodeURIComponent(String(id))),
        );
      } else {
        navigate(AppRoutes.Order);
      }
    }
  }, [orderReady, orderData, id, navigate]);

  const numBk = (v: unknown) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const formatMoney = (value: unknown) => {
    const converted = CurrencyConvertHistory(numBk(value), currency);
    return `${CurrencyIcon(currency)}${getPriceHelper(converted)}`;
  };

  const taxAmount = useMemo(
    () => getQuotationTaxAmount(quotation),
    [quotation],
  );

  const itemCount = useMemo(() => {
    const fromApi = orderData?.item_count;
    if (fromApi != null && Number(fromApi) > 0) return Number(fromApi);
    return getCheckoutItemCount(quotation);
  }, [orderData?.item_count, quotation]);

  const otherChargesLines = useMemo(
    () => parseOtherChargesLines(quotation?.charges_reason),
    [quotation?.charges_reason],
  );
  const otherChargesTotal = numBk(quotation?.other_charges);
  const hasOtherChargesDetail =
    otherChargesLines.length > 0 || otherChargesTotal > 0;

  const longDate = formatDisplayLongDate(orderData?.created_at, "—");

  const deliveryAddressLine = buildOrderDeliveryAddressLines(orderData);
  const instructionsText = getOrderShippingInstructions(orderData);
  const residenceLine = getOrderResidenceLabel(orderData);

  const orderDetailPath = id
    ? AppRoutes.OrderDetail.replace(":id", encodeURIComponent(String(id)))
    : AppRoutes.Order;

  const showWireTransfer =
    (orderData?.total_amount ?? quotation?.total_amount) != null &&
    Number(orderData?.total_amount ?? quotation?.total_amount) >= 20000;

  const handlePayment = () => {
    if (!payMethod) {
      setError(
        LanguageConstants?.SELETE_PAYMENT_ERROR ?? "Please select payment method",
      );
      return;
    }
    if (!orderData?.uuid) return;
    const payload = {
      order_id: orderData.uuid,
      payment_type: payMethod,
      success_url: Config.REACT_APP_SUCCESSFULLY,
      cancel_url: Config.REACT_APP_FAILED,
      type: "web",
    };
    retryPaymentReq.makeRequest({
      payload,
      url: ApiConstants?.OrderRetryPayment,
      toastStatus: false,
    });
    setError("");
  };

  useEffect(() => {
    if (!retryPaymentReq?.status) return;
    if (retryPaymentReq.data?.data?.url) {
      const sessionId = retryPaymentReq.data?.data?.id;
      if (sessionId) saveStripeCheckoutSessionId(String(sessionId));
      window.location.href = retryPaymentReq.data.data.url;
      return;
    }
    if (retryPaymentReq.data?.data?.wire_transfer) {
      if (wireSuccessShownRef.current) return;
      wireSuccessShownRef.current = true;
      socket.emit("admin_count", { sender_type: "1", receiver_id: "1" }, async () => {});
      setWireSuccessOpen(true);
      return;
    }
    setError(
      LanguageConstants?.Payment_Failed2 ??
        "Unable to start payment. Please try again.",
    );
  }, [retryPaymentReq?.status]);

  const renderPlaceOrderButton = (className?: string) => (
    <div className="row mt-5">
      <div className="col-11 col-sm-7 mx-auto">
        <button
          type="button"
          className={`round-btn w-100${className ? ` ${className}` : ""}`}
          onClick={handlePayment}
          disabled={retryPaymentReq?.loading || !orderReady}
        >
          {LanguageConstants?.CONTINUE_TO_PAYMENT ?? "Continue to Payment"}
        </button>
      </div>
    </div>
  );

  if (!orderUuid) {
    return (
      <div className="head-space">
        <div className="inner-content">
          <div className="quote-checkout-page">
            <div className="qc-page__shell">
              <p className="qc-page__empty">
                {LanguageConstants?.DATANOTFOUND ?? "Not found"}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {(showLoader || retryPaymentReq?.loading) && <FrontLoader />}
      <div className="head-space">
        <div className="inner-content">
          <div className="quote-checkout-page">
            <div className="container">
              <header className="qc-page__header">
                <button
                  type="button"
                  className="qc-page__navBack"
                  onClick={() => navigate(orderDetailPath)}
                  aria-label={`${LanguageConstants?.BACK ?? "Back"} — ${LanguageConstants?.ORDER_DETAILS_HEAD ?? "Order Details"}`}
                >
                  <i className="icon-chev-right qc-page__navBackIcon" aria-hidden />
                  {LanguageConstants?.CHECKOUT ?? "Checkout"}
                </button>

                {orderReady ? (
                  <div className="qc-page__quoteMeta">
                    <p className="qc-page__metaLine qc-page__metaLine--primary">
                      {LanguageConstants?.ORDER_DETAILS_HEAD ?? "Order Details"}:{" "}
                      <span className="qc-page__metaValue">{orderData?.uuid}</span>
                    </p>
                    <p className="qc-page__metaLine">
                      {LanguageConstants?.VIEW_QUOTE_DATE_LABEL ?? "Date"}:{" "}
                      <span className="qc-page__metaValue">{longDate}</span>
                    </p>
                    <p className="qc-page__metaLine">
                      {LanguageConstants?.VIEW_QUOTE_ITEMS_COUNT ?? "Items"}{" "}
                      <span className="qc-page__metaValue">{itemCount}</span>
                    </p>
                  </div>
                ) : null}
              </header>

              {orderReady ? (
                <>
                  <div className="qc-page__columns">
                    <section
                      className="qc-card qc-card--pricing"
                      aria-labelledby="qc-breakdown-title"
                    >
                      <div className="qc-breakdown__head">
                        <h2 id="qc-breakdown-title" className="qc-card__sectionTitle">
                          {LanguageConstants?.PRICE_BRACKDOWN ?? "Price Breakdown"}
                        </h2>
                        <label className="qc-breakdown__toggle">
                          <span className="qc-breakdown__switch">
                            <input
                              type="checkbox"
                              checked={currency === "EUR"}
                              onChange={(e) =>
                                setCurrency(e.target.checked ? "EUR" : "USD")
                              }
                              aria-label={LanguageConstants?.CONVERTINPISO}
                            />
                            <span className="qc-breakdown__switchTrack" aria-hidden />
                          </span>
                          <span className="qc-breakdown__toggleText">
                            {LanguageConstants?.CONVERTINPISO ??
                              "Convert prices in euro"}
                          </span>
                        </label>
                      </div>

                      <div className="qc-breakdown__rows">
                        <div className="qc-breakdown__row">
                          <span>
                            {LanguageConstants?.TOTAL_PRODUCT_PRICE ??
                              LanguageConstants?.TOTALPRODUCTAMOUNT}
                          </span>
                          <span className="qc-breakdown__rowVal">
                            {formatMoney(quotation?.total_product_price)}
                          </span>
                        </div>
                        <div className="qc-breakdown__row">
                          <span>
                            {LanguageConstants?.SHIPPINGPRICE ??
                              LanguageConstants?.TOTALSHIIPNGAMOUNT}
                          </span>
                          <span className="qc-breakdown__rowVal">
                            {formatMoney(quotation?.total_shipping_price)}
                          </span>
                        </div>
                        <div className="qc-breakdown__row">
                          <span className="qc-breakdown__rowLabel">
                            {LanguageConstants?.OTHER_CARGERS}
                            <button
                              type="button"
                              className={`qc-breakdown__info${
                                hasOtherChargesDetail
                                  ? ""
                                  : " qc-breakdown__info--disabled"
                              }`}
                              disabled={!hasOtherChargesDetail}
                              aria-disabled={!hasOtherChargesDetail}
                              title={
                                hasOtherChargesDetail
                                  ? LanguageConstants?.VIEW_QUOTE_OTHER_CHARGES_BTN ??
                                    "View other charges breakdown"
                                  : LanguageConstants?.VIEW_QUOTE_OTHER_CHARGES_NONE ??
                                    "No other charges details"
                              }
                              aria-label={
                                LanguageConstants?.Charges_reason ??
                                "Other charges details"
                              }
                              onClick={() => {
                                if (hasOtherChargesDetail) {
                                  setOtherChargesModalOpen(true);
                                }
                              }}
                            >
                              <i className="icon-info" aria-hidden />
                            </button>
                          </span>
                          <span className="qc-breakdown__rowVal">
                            {formatMoney(quotation?.other_charges ?? 0)}
                          </span>
                        </div>
                        <div className="qc-breakdown__row">
                          <span>{LanguageConstants?.TOTALTAX}</span>
                          <span className="qc-breakdown__rowVal">
                            {formatMoney(taxAmount)}
                          </span>
                        </div>
                      </div>

                      <div className="qc-breakdown__divider" />

                      <div className="qc-breakdown__grand">
                        <span className="qc-breakdown__grandLabel">
                          {LanguageConstants?.GRANDTOTAL ?? "Grand Total"}
                        </span>
                        <div className="qc-breakdown__grandRight">
                          <p className="qc-breakdown__usdNote">
                            {currency === "EUR"
                              ? LanguageConstants?.VIEW_QUOTE_FOLLOW_EUR ??
                                "Following price is in EUR"
                              : LanguageConstants?.PRIZEMESSAGE}
                          </p>
                          <span className="qc-breakdown__grandAmt">
                            {formatMoney(
                              orderData?.total_amount ?? quotation?.total_amount,
                            )}
                          </span>
                        </div>
                      </div>

                      <div className="qc-paymentBlock">
                        <h3 className="qc-paymentBlock__title">
                          {LanguageConstants?.PAYMENT_MATHOD ?? "Payment Method"}
                        </h3>
                        <div className="qc-payment">
                          <label className="qc-payment__option">
                            <input
                              className="qc-payment__radio"
                              type="radio"
                              name="paymentMethod"
                              checked={payMethod === "0"}
                              onChange={() => setPayMethod("0")}
                            />
                            <span className="qc-payment__label">
                              {LanguageConstants?.ONLINE ?? "Online"}
                            </span>
                          </label>

                          {showWireTransfer ? (
                            <label className="qc-payment__option">
                              <input
                                className="qc-payment__radio"
                                type="radio"
                                name="paymentMethod"
                                checked={payMethod === "1"}
                                onChange={() => setPayMethod("1")}
                              />
                              <span className="qc-payment__label">
                                {LanguageConstants?.OFFLIE ?? "Bank wire transfer"}
                              </span>
                            </label>
                          ) : null}
                        </div>
                        {error ? <p className="qc-page__error">{error}</p> : null}
                      </div>

                      {renderPlaceOrderButton()}
                    </section>

                    <section
                      className="qc-card qc-card--shipping"
                      aria-labelledby="qc-shipping-title"
                    >
                      <div className="qc-shipping__head" id="qc-shipping-title">
                        <img src={truckIcon} alt="" className="qc-shipping__headIcon" />
                        <span className="qc-shipping__headText">
                          {LanguageConstants?.MANUAL_QUOTE_SHIPPING_DETAILS ??
                            "Shipping Details"}
                        </span>
                      </div>

                      <div className="qc-shipping__body">
                        <div className="qc-shipping__rows">
                          <div className="qc-shipping__row">
                            <div className="qc-shipping__label">
                              {LanguageConstants?.MANUAL_QUOTE_DELIVERY_SECTION ??
                                "Delivery Address"}
                            </div>
                            <p className="qc-shipping__value">
                              {deliveryAddressLine}
                            </p>
                          </div>

                          <div className="qc-shipping__row">
                            <div className="qc-shipping__label">
                              {LanguageConstants?.MANUAL_QUOTE_INSTRUCTIONS ??
                                LanguageConstants?.INSTRUCTIONS ??
                                "Instructions"}
                            </div>
                            <p className="qc-shipping__value">{instructionsText}</p>
                          </div>

                          <div className="qc-shipping__row">
                            <div className="qc-shipping__label">
                              {LanguageConstants?.MANUAL_QUOTE_RESIDENCE ??
                                "Type of Residence"}
                            </div>
                            <p className="qc-shipping__value">{residenceLine}</p>
                          </div>

                          <div className="qc-shipping__row">
                            <div className="qc-shipping__label">
                              {LanguageConstants?.MANUAL_QUOTE_SHIPPING_METHOD_SECTION ??
                                "Shipping Method"}
                            </div>
                            <p className="qc-shipping__value">
                              {quotation?.shipping_Trans_detail?.name ?? "—"}
                            </p>
                          </div>
                        </div>
                      </div>
                    </section>
                  </div>
                </>
              ) : orderFetchSettled ? (
                <p className="qc-page__empty">
                  {LanguageConstants?.DATANOTFOUND ?? "Not found"}
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <OrderSuccessOverlay show={wireSuccessOpen} isWireTransfer />

      <Modal
        show={otherChargesModalOpen}
        onHide={() => setOtherChargesModalOpen(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {LanguageConstants?.VIEW_QUOTE_OTHER_CHARGES_TITLE ??
              "Other Charges Breakdown"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="qc-otherChargesModal">
            {otherChargesLines.length > 0 ? (
              <ul className="qc-otherChargesModal__list">
                {otherChargesLines.map((line, idx) => (
                  <li
                    key={`${line.label}-${idx}`}
                    className="qc-otherChargesModal__row"
                  >
                    <span className="qc-otherChargesModal__label">
                      {line.label}
                    </span>
                    <span className="qc-otherChargesModal__amt">
                      {CurrencyIcon(currency)}
                      {getPriceHelper(
                        CurrencyConvertHistory(
                          parseChargeLineAmount(line.amount),
                          currency,
                        ),
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="qc-otherChargesModal__empty">
                {LanguageConstants?.VIEW_QUOTE_OTHER_CHARGES_EMPTY ??
                  "No itemized charges recorded."}
              </p>
            )}
            <div className="qc-otherChargesModal__total">
              <span>{LanguageConstants?.OTHER_CARGERS}</span>
              <span className="qc-otherChargesModal__totalAmt">
                {CurrencyIcon(currency)}
                {getPriceHelper(
                  CurrencyConvertHistory(otherChargesTotal, currency),
                )}
              </span>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default OrderCheckout;
